import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';

interface HideAmountsContextType {
  hideAmounts: boolean;
  toggleHideAmounts: () => void;
  saving: boolean;
  /** Wrap any euro/currency value — returns blurred span or "•••" when hidden */
  formatAmount: (value: string | number) => string;
  /** CSS class to apply blur to amounts */
  amountClassName: string;
}

const HideAmountsContext = createContext<HideAmountsContextType>({
  hideAmounts: false,
  toggleHideAmounts: () => {},
  saving: false,
  formatAmount: (v) => String(v),
  amountClassName: '',
});

export function HideAmountsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const prefs = (user?.preferences ?? {}) as Record<string, any>;
  const [hideAmounts, setHideAmounts] = useState<boolean>(!!prefs.hide_amounts);
  const [saving, setSaving] = useState(false);

  // Sync with user preferences when they change
  useEffect(() => {
    setHideAmounts(!!((user?.preferences ?? {}) as Record<string, any>).hide_amounts);
  }, [user?.preferences]);

  const toggleHideAmounts = useCallback(async () => {
    const newValue = !hideAmounts;
    setHideAmounts(newValue);
    setSaving(true);
    try {
      await api.patch(`${ROUTES.API.USERS}update_preferences/`, {
        preferences: { hide_amounts: newValue },
      });
      await useAuth.getState().checkAuth();
    } catch {
      setHideAmounts(!newValue);
    } finally {
      setSaving(false);
    }
  }, [hideAmounts]);

  const formatAmount = useCallback(
    (value: string | number): string => {
      if (hideAmounts) return '•••';
      return String(value);
    },
    [hideAmounts],
  );

  const amountClassName = hideAmounts ? 'blur-md select-none' : '';

  return (
    <HideAmountsContext.Provider
      value={{ hideAmounts, toggleHideAmounts, saving, formatAmount, amountClassName }}
    >
      {children}
    </HideAmountsContext.Provider>
  );
}

export function useHideAmounts() {
  return useContext(HideAmountsContext);
}
